import { SCOrderListResponseType } from "@sellar-io/api-v2/types";
import {
  Badge,
  BadgeGreen,
  BadgeRed,
  BadgeWhite,
  BadgeZinc,
} from "../../../../../../components/badge/Badge";
import dayjs from "dayjs";
import type { ComponentType } from "react";
import type { BadgeProps } from "../../../../../../components/badge/Badge";

type Status =
  | "pending"
  | "added"
  | "issued"
  | "paid"
  | "processing"
  | "overdue"
  | "cancelled";

interface StatusCondition {
  status: Status;
  condition: (_context: StatusContext) => boolean;
}

interface StatusContext {
  order: SCOrderListResponseType[number];
  orderBuyerInvoice:
    | SCOrderListResponseType[number]["Invoices"][number]
    | undefined;
  today: dayjs.Dayjs;
  dispatched: dayjs.Dayjs;
  dueDate: dayjs.Dayjs;
}

const statusConditions: StatusCondition[] = [
  {
    status: "cancelled",
    condition: ({ order }) => !!order.cancelledAt,
  },
  {
    status: "paid",
    condition: ({ order }) => !!order.retailerPaidAt,
  },
  {
    status: "overdue",
    condition: ({ orderBuyerInvoice, today }) =>
      !!orderBuyerInvoice && today.isAfter(dayjs(orderBuyerInvoice.dueAt)),
  },
  {
    status: "processing",
    condition: ({ today, dueDate }) =>
      today.isAfter(dueDate) && today.isBefore(dayjs(dueDate).add(4, "days")),
  },
  {
    status: "issued",
    condition: ({ today, dispatched }) =>
      today.isAfter(dispatched.endOf("month")),
  },
  {
    status: "added",
    condition: ({ order }) => !!order.dispatchedAt,
  },
];

function determineStatus(ctx: StatusContext): Status {
  return (
    statusConditions.find(({ condition }) => condition(ctx))?.status ??
    "pending"
  );
}

export const TabPaymentStatusBadge = ({
  order,
}: {
  order: SCOrderListResponseType[number];
}) => {
  const orderBuyerInvoice = order.Invoices.find(
    (invoice) =>
      invoice.type === "sellarCreditRetailer" && invoice.status !== "void",
  );

  const today = dayjs();
  const dispatched = dayjs(order.dispatchedAt);
  const dueDate = dispatched.endOf("month").add(30, "days");

  const status = determineStatus({
    order,
    orderBuyerInvoice,
    today,
    dispatched,
    dueDate,
  });

  const statusConfig: Record<
    Status,
    { component: ComponentType<BadgeProps>; label: string }
  > = {
    added: { component: Badge, label: "Added to statement" },
    issued: {
      component: BadgeZinc,
      label: `Issued: Due ${dueDate.format("MMM DD")}`,
    },
    paid: { component: BadgeGreen, label: "Paid" },
    overdue: { component: BadgeRed, label: "Overdue" },
    processing: { component: BadgeZinc, label: "Processing" },
    cancelled: { component: BadgeZinc, label: "Void" },
    pending: { component: BadgeWhite, label: "Pending" },
  };

  const { component: BadgeVariant, label } = statusConfig[status];

  return <BadgeVariant>{label}</BadgeVariant>;
};
